import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, Search, FileText, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { GrokLogo } from "@/components/grok-logo";

interface Document {
  id: string;
  filename: string;
  product: string;
  status: "uploaded" | "parsing" | "chunking" | "indexing" | "ready" | "error";
  chunks: ChunkInfo[];
  error?: string;
  uploadedAt: string;
}

interface ChunkInfo {
  index: number;
  content: string;
  charCount: number;
}

const PRODUCTS = [
  "FHA",
  "Conventional",
  "VA",
  "USDA",
  "Jumbo",
  "HELOC",
  "Reverse",
  "Non-QM",
  "Exceptions",
];

const STATUS_COLORS = {
  uploaded: "bg-[#666666]",
  parsing: "bg-[#666666]",
  chunking: "bg-[#666666]",
  indexing: "bg-[#666666]",
  ready: "bg-[#666666]",
  error: "bg-[#666666]",
};

export default function LibraryPage() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<string>("all");
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());
  const [selectedChunk, setSelectedChunk] = useState<{
    docId: string;
    chunkIndex: number;
    content: string;
  } | null>(null);
  const [uploadingProduct, setUploadingProduct] = useState<string | null>(null);
  const { toast } = useToast();

  const handleFileUpload = async (
    product: string,
    file: File
  ) => {
    setUploadingProduct(product);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("product", product);
      formData.append("document_type", "guideline");

      const response = await fetch("/api/admin/documents/upload", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const result = await response.json();
      
      // Add document to state
      const newDoc: Document = {
        id: result.document_id,
        filename: file.name,
        product,
        status: "uploaded",
        chunks: [],
        uploadedAt: new Date().toISOString(),
      };

      setDocuments((prev) => [...prev, newDoc]);
      
      // Start processing
      processDocument(newDoc.id);
      
      toast({
        title: "Upload Successful",
        description: `Processing ${file.name}...`,
      });
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setUploadingProduct(null);
    }
  };

  const processDocument = async (docId: string) => {
    // Update status to parsing
    updateDocumentStatus(docId, "parsing");
    
    // Simulate parsing
    await sleep(1000);
    
    // Update status to chunking
    updateDocumentStatus(docId, "chunking");
    
    // Simulate chunking
    await sleep(2000);
    
    // Get chunks
    const chunks = await fetchChunks(docId);
    
    // Update document with chunks
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === docId ? { ...doc, chunks, status: "indexing" } : doc
      )
    );
    
    // Simulate indexing
    await sleep(1500);
    
    // Set to ready
    updateDocumentStatus(docId, "ready");
  };

  const updateDocumentStatus = (
    docId: string,
    status: Document["status"]
  ) => {
    setDocuments((prev) =>
      prev.map((doc) => (doc.id === docId ? { ...doc, status } : doc))
    );
  };

  const fetchChunks = async (docId: string) => {
    // This would fetch actual chunks from the API
    // For now, return mock chunks
    return [
      { index: 0, content: "Chunk 1 content...", charCount: 100 },
      { index: 1, content: "Chunk 2 content...", charCount: 150 },
      { index: 2, content: "Chunk 3 content...", charCount: 200 },
    ];
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const toggleDocumentExpansion = (docId: string) => {
    setExpandedDocs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  const handleChunkClick = (docId: string, chunk: ChunkInfo) => {
    setSelectedChunk({ docId, chunkIndex: chunk.index, content: chunk.content });
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      searchQuery === "" ||
      doc.filename.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProduct =
      selectedProduct === "all" || doc.product === selectedProduct;
    return matchesSearch && matchesProduct;
  });

  return (
    <div 
      className="min-h-screen bg-white" 
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', 
        background: '#ffffff',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)'
      }}
    >
      <header 
        className="px-4 sm:px-6 h-14 border-b border-[#e0e0e0] bg-white sticky top-0 z-50"
        style={{ 
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          height: 'calc(56px + env(safe-area-inset-top, 0px))',
          paddingLeft: 'calc(16px + env(safe-area-inset-left, 0px))',
          paddingRight: 'calc(16px + env(safe-area-inset-right, 0px))'
        }}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center h-full">
          <div className="flex items-center gap-4">
            <GrokLogo size={32} className="flex-shrink-0" />
            <h1 className="text-xl font-semibold text-[#1a1a1a]" style={{ textAlign: 'left' }}>Document Library</h1>
          </div>
        </div>
      </header>
      <div className="container mx-auto p-4 sm:p-6 space-y-6 max-w-7xl" style={{ textAlign: 'left' }}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <Input
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-64 border border-[#e0e0e0] bg-[#f5f5f5] text-[#1a1a1a] placeholder:text-[#b0b0b0] focus:border-[#d0d0d0] focus:ring-1 focus:ring-[#d4d4d4] focus:outline-none"
              style={{ 
                fontSize: '16px',
                textAlign: 'left',
                WebkitAppearance: 'none',
                appearance: 'none',
                minHeight: '44px',
                padding: '12px 16px'
              }}
            />
            <Select value={selectedProduct} onValueChange={setSelectedProduct}>
              <SelectTrigger 
                className="w-full sm:w-48 border border-[#e0e0e0] bg-white text-[#1a1a1a] touch-manipulation"
                style={{ 
                  fontSize: '16px',
                  textAlign: 'left',
                  minHeight: '44px',
                  padding: '12px 16px'
                }}
              >
                <SelectValue placeholder="Filter by product" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-[#e0e0e0]">
                <SelectItem value="all" className="text-[#1a1a1a] hover:bg-[#f5f5f5]" style={{ fontSize: '16px', minHeight: '44px' }}>All Products</SelectItem>
                {PRODUCTS.map((product) => (
                  <SelectItem key={product} value={product} className="text-[#1a1a1a] hover:bg-[#f5f5f5]" style={{ fontSize: '16px', minHeight: '44px' }}>
                    {product}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {PRODUCTS.map((product) => (
          <Card key={product} className="relative rounded-lg border border-[#e0e0e0] bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-[#1a1a1a]">
                <span>{product}</span>
                <Badge variant="outline" className="border-[#e0e0e0] text-[#666666]">
                  {documents.filter((d) => d.product === product).length} docs
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Upload Button */}
              <label>
                <input
                  type="file"
                  accept=".pdf,.docx,.txt"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleFileUpload(product, file);
                    }
                  }}
                  disabled={uploadingProduct === product}
                />
                <Button
                  className="w-full border border-[#e0e0e0] text-[#1a1a1a] hover:bg-[#f5f5f5] hover:border-[#1a1a1a] bg-white touch-manipulation"
                  variant="outline"
                  disabled={uploadingProduct === product}
                  style={{ minHeight: '44px', fontSize: '16px', textAlign: 'center' }}
                >
                  {uploadingProduct === product ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  Upload Document
                </Button>
              </label>

              {/* Product Documents */}
              {filteredDocuments
                .filter((d) => d.product === product)
                .map((doc) => (
                  <Card
                    key={doc.id}
                    className="border border-[#e0e0e0] cursor-pointer transition-colors rounded-lg bg-white hover:border-[#1a1a1a]"
                  >
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm truncate text-[#1a1a1a]">
                          {doc.filename}
                        </span>
                        <Badge className={STATUS_COLORS[doc.status]}>
                          {doc.status}
                        </Badge>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleDocumentExpansion(doc.id)}
                        className="w-full text-xs text-[#666666] hover:text-[#1a1a1a] hover:bg-[#f5f5f5] touch-manipulation"
                        style={{ minHeight: '44px', fontSize: '14px', textAlign: 'center' }}
                      >
                        {expandedDocs.has(doc.id) ? "Hide" : "Show"} Chunks ({doc.chunks.length})
                      </Button>

                      {expandedDocs.has(doc.id) && (
                        <div className="space-y-1 pt-2">
                          {doc.chunks.map((chunk) => (
                            <div
                              key={chunk.index}
                              className="text-xs p-2 rounded cursor-pointer border border-[#e0e0e0] transition-colors bg-[#f5f5f5] hover:border-[#1a1a1a] touch-manipulation"
                              onClick={() => handleChunkClick(doc.id, chunk)}
                              style={{ minHeight: '44px', textAlign: 'left' }}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-[#1a1a1a]">Chunk {chunk.index}</span>
                                <span className="text-[#9b9b9b]">{chunk.charCount} chars</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Chunk Side Panel */}
      {selectedChunk && (
        <Card className="fixed right-0 top-0 h-full w-96 border-l border-[#e0e0e0] shadow-lg z-50 rounded-none bg-white">
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-[#1a1a1a]">
              <span>Chunk {selectedChunk.chunkIndex}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedChunk(null)}
                className="text-[#666666] hover:text-[#1a1a1a] hover:bg-[#f5f5f5] touch-manipulation"
                style={{ minHeight: '44px', minWidth: '44px', fontSize: '20px', textAlign: 'center' }}
              >
                ×
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap text-sm text-[#1a1a1a]">
              {selectedChunk.content}
            </pre>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
}

